// src/pages/MarketplaceIntegration/useMarketplaceIntegration.js

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import integrationService from './integrationService';
import notificationService from './notificationService';

// Custom hook for marketplace integration functionality
const useMarketplaceIntegration = () => {
  const { userData } = useAuth();
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);

  // Fetch all marketplace connections
  const fetchMarketplaces = useCallback(async () => {
    if (!userData?.companyId) return;

    try {
      setLoading(true);
      const data = await integrationService.getMarketplaceConnections();
      setMarketplaces(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching marketplaces:', err);
      setError('Pazaryeri bağlantıları alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [userData?.companyId]);

  // Initialize the integration service
  useEffect(() => {
    if (!userData?.companyId) return;

    const initService = async () => {
      try {
        await integrationService.initialize(userData.companyId);
        await fetchMarketplaces();
        setLoading(false);
      } catch (err) {
        console.error('Error initializing integration service:', err);
        setError('Entegrasyon servisi başlatılırken hata oluştu');
        setLoading(false);
      }
    };

    initService();

    // Subscribe to notifications
    const notificationListenerId = notificationService.subscribe('all', (notification) => {
      setNotifications(prev => [notification, ...prev.slice(0, 49)]); // Keep last 50 notifications
    });

    // Cleanup
    return () => {
      if (notificationListenerId) {
        notificationService.unsubscribe('all', notificationListenerId);
      }
    };
  }, [userData?.companyId, fetchMarketplaces]);

  // Test marketplace connection
  const testConnection = useCallback(async (marketplaceId) => {
    try {
      const result = await integrationService.testMarketplaceConnection(marketplaceId);
      // Refresh marketplace data
      await fetchMarketplaces();
      return result;
    } catch (err) {
      console.error('Error testing connection:', err);
      setError('Bağlantı testi sırasında hata oluştu');
      throw err;
    }
  }, [fetchMarketplaces]);

  // Sync a single marketplace
  const syncMarketplace = useCallback(async (marketplaceId) => {
    try {
      setSyncing(true);
      const result = await integrationService.syncMarketplace(marketplaceId);
      // Refresh marketplace data
      await fetchMarketplaces();
      return result;
    } catch (err) {
      console.error('Error syncing marketplace:', err);
      setError('Senkronizasyon sırasında hata oluştu');
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [fetchMarketplaces]);

  // Sync all marketplaces
  const syncAllMarketplaces = useCallback(async () => {
    try {
      setSyncing(true);
      await integrationService.syncAllMarketplaces();
      // Refresh marketplace data
      await fetchMarketplaces();
    } catch (err) {
      console.error('Error syncing all marketplaces:', err);
      setError('Toplu senkronizasyon sırasında hata oluştu');
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [fetchMarketplaces]);

  // Add new marketplace connection
  const addMarketplace = useCallback(async (marketplaceData) => {
    if (!userData?.companyId) return;

    try {
      // Use integration service to add marketplace
      // This should be implemented in integrationService
      await integrationService.addMarketplace(marketplaceData);

      // Refresh marketplace data
      await fetchMarketplaces();
    } catch (err) {
      console.error('Error adding marketplace:', err);
      setError('Yeni pazaryeri bağlantısı eklenirken hata oluştu');
      throw err;
    }
  }, [userData?.companyId, fetchMarketplaces]);

  // Update marketplace connection
  const updateMarketplace = useCallback(async (id, marketplaceData) => {
    if (!userData?.companyId) return;

    try {
      // Use integration service to update marketplace
      await integrationService.updateMarketplace(id, marketplaceData);

      // Refresh marketplace data
      await fetchMarketplaces();
    } catch (err) {
      console.error('Error updating marketplace:', err);
      setError('Pazaryeri bağlantısı güncellenirken hata oluştu');
      throw err;
    }
  }, [userData?.companyId, fetchMarketplaces]);

  // Delete marketplace connection
  const deleteMarketplace = useCallback(async (id) => {
    if (!userData?.companyId) return;

    try {
      // Use integration service to delete marketplace
      await integrationService.deleteMarketplace(id);

      // Refresh marketplace data
      await fetchMarketplaces();
    } catch (err) {
      console.error('Error deleting marketplace:', err);
      setError('Pazaryeri bağlantısı silinirken hata oluştu');
      throw err;
    }
  }, [userData?.companyId, fetchMarketplaces]);

  // Get marketplace products
  const getMarketplaceProducts = useCallback(async (marketplaceId) => {
    try {
      const products = await integrationService.getMarketplaceProducts(marketplaceId);
      return products;
    } catch (err) {
      console.error('Error fetching marketplace products:', err);
      setError('Pazaryeri ürünleri alınırken hata oluştu');
      throw err;
    }
  }, []);

  // Get marketplace orders
  const getMarketplaceOrders = useCallback(async (marketplaceId) => {
    try {
      const orders = await integrationService.getMarketplaceOrders(marketplaceId);
      return orders;
    } catch (err) {
      console.error('Error fetching marketplace orders:', err);
      setError('Pazaryeri siparişleri alınırken hata oluştu');
      throw err;
    }
  }, []);

  // Get error logs
  const getErrorLogs = useCallback(async (filters = {}) => {
    try {
      const errors = await integrationService.getErrorLogs(filters);
      return errors;
    } catch (err) {
      console.error('Error fetching error logs:', err);
      setError('Hata raporları alınırken hata oluştu');
      throw err;
    }
  }, []);

  // Mark notification as read
  const markNotificationAsRead = useCallback((notificationId) => {
    notificationService.markAsRead(notificationId);
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  // Get unread notifications count
  const getUnreadNotificationsCount = useCallback(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  return {
    marketplaces,
    loading,
    syncing,
    notifications,
    error,
    fetchMarketplaces,
    testConnection,
    syncMarketplace,
    syncAllMarketplaces,
    addMarketplace,
    updateMarketplace,
    deleteMarketplace,
    getErrorLogs,
    markNotificationAsRead,
    getUnreadNotificationsCount,
    getMarketplaceProducts,
    getMarketplaceOrders
  };
};

export default useMarketplaceIntegration;